<template>
  <div id="sticky-show" class="buybox">
    <BuyboxGallery 
      :product="product"
      :selectedVariant="selectedVariant"
      :isTest="isTest"
    />
    <div class="buybox__content">
      <h1 v-if="product.title" class="product__title">{{ product.title }}</h1>
      <h1 v-else class="product__title">{{ selectedVariant.airtable_title }}</h1>
      <div v-if="product.subtitle" class="product__subtitle text-xl">{{ product.subtitle }}</div>
      <div v-else class="product__subtitle text-xl">{{ selectedVariant.airtable_subtitle }}</div>
      <div v-if="yotpoProductId" class="product__reviews">
        <div class="yotpo bottomLine" data-appkey="SmtoQBJliJwFrBjSYjmEtOz31o6nMoLZfgcp3J4j" :data-product-id="yotpoProductId"></div>
      </div>
      <div v-if="product.description" class="product__description" v-html="product.description"></div>
      <div v-else class="product__description" v-html="selectedVariant.airtable_excerpt"></div>
      <BuyboxOptions
        :product="product"
        :selectedVariant="selectedVariant"
        :isTest="isTest"
        @update-option="onUpdateOption"
      />
      <BuyboxSubscription 
        ref="buyboxSubscription"
        :product="product"
        :selectedVariant="selectedVariant"
        :isMultiPack="isMultiPack"
        :selectedSellingPlan="selectedSellingPlan"
        :isTest="isTest"
        @update-option="onUpdateOption"
        @update-selling-plan="onUpdateSellingPlan"
        @update-is-subscription="onUpdateIsSubscription"
      />
      <div v-if="product.freebie" class="buybox__freebie">
        <svg class="buybox__freebie-icon">
          <use href="#freebie"></use>
        </svg>
        <span class="buybox__freebie-text" v-html="product.freebie.callout_text"></span>
      </div>
      <div v-if="shopLocation !== 'GB' && !product.customer && selectedVariant.available && !selectedVariant.sold_out" class="buybox__customer-login">
        <span @click="emit('toggle-uc-ship-modal')" class="buybox__link">Sign in</span> to add this product to your next shipment.
      </div>
      <BuyboxATC
        :product="product"
        :isMultiPack="isMultiPack"
        :isSubscription="buyboxSubscription?.isSubscription"
        :selectedVariant="selectedVariant"
        :selectedSellingPlan="selectedSellingPlan"
        :isTest="isTest"
        @track-event="trackEvent"
      />

      <div class="truemed-widget">
        <div shopify-tags="display-unless-ineligible" id="truemed-prequalify" icon-height="16" data-public-id="tm_qual_bjv51prpsl"></div>
      </div>
      <component
        src="https://truemed-public.s3.us-west-1.amazonaws.com/truemed-ads/prequal-widget.min.js"
        :is="'script'"
        defer
    ></component>

      <div v-if="!isTest && product.announcements && product.announcements.items.length" class="buybox__announcements">
        <div v-for="announcement in product.announcements.items" class="buybox__announcement">
          <svg class="buybox__announcement-icon">
            <use href="#green-tick"></use>
          </svg>
          <span class="buybox__announcement-text">{{ announcement }}</span>
        </div>
      </div>
      <div v-else-if="!isTest" class="buybox__announcements">
        <div class="buybox__announcement">
          <svg class="buybox__announcement-icon">
            <use href="#green-tick"></use>
          </svg>
          <span class="buybox__announcement-text">Free 2-3 day shipping over $75</span>
        </div>
        <div class="buybox__announcement">
          <svg class="buybox__announcement-icon">
            <use href="#green-tick"></use>
          </svg>
          <span class="buybox__announcement-text">100% satisfaction guarantee</span>
        </div>
      </div>

      <div v-else-if="product.template === 'test-buy-box' && product.announcements.items.length" :data-items="product.announcements.items.length" class="buybox__announcements">
        <div v-for="announcement in product.announcements.items" class="buybox__announcement">
          <span class="buybox__announcement-text">{{ announcement }}</span>
        </div>
      </div>
      <div v-else-if="product.template === 'test-buy-box'" data-items="2" class="buybox__announcements">
        <div class="buybox__announcement">
          <span class="buybox__announcement-text">Free 2-3 day shipping over $75</span>
        </div>
        <div class="buybox__announcement">
          <span class="buybox__announcement-text">100% satisfaction guarantee</span>
        </div>
      </div>

      <div v-if="!selectedVariant.available || selectedVariant.sold_out" class="buybox__nwis text-sm">
        <span @click="emit('toggle-bis-modal')" class="buybox__link">Get notified</span> when this product is back in stock!
      </div>
      <div class="buybox__curated-reviews--mobile">
        <BuyboxCuratedReviews
          :product="product"
        />
      </div>
      <BuyboxAccor
        :accordians="accordianData"
      />
    </div>
  </div>
  <BuyboxStickyBar
    :product="product"
    :selectedVariant="selectedVariant"
    :isMultiPack="isMultiPack"
    :yotpoProductId="yotpoProductId"
    :selectedSellingPlan="selectedSellingPlan"
    :isSubscription="buyboxSubscription?.isSubscription"
    @update-option="onUpdateOption"
    @update-selling-plan="onUpdateSellingPlan"
    @update-is-subscription="onUpdateIsSubscription"
    @track-event="trackEvent"
  />
</template>

<script setup>
  import { ref, reactive, onBeforeMount, onMounted, computed } from 'vue';
  import BuyboxGallery from "./buybox-gallery.vue";
  import BuyboxOptions from './buybox-options.vue';
  import BuyboxSubscription from './buybox-subscription.vue';
  import BuyboxATC from "./buybox-atc.vue";
  import BuyboxStickyBar from "./buybox-sticky-bar.vue";
  import BuyboxCuratedReviews from './buybox-curated-reviews.vue';
  import BuyboxAccor from './buybox-accor.vue';

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({})
    }
  })

  const product = reactive({ ...props.product });
  const selectedOptionIndex = ref(0);
  const selectedVariant = ref(null);
  const selectedSellingPlan = ref(null);
  const multiPackOptions = ['boxes', 'tubs', 'bottles'];
  const buyboxSubscription = ref(null);
  const emit = defineEmits(['toggle-bis-modal','toggle-uc-ship-modal'])

  onBeforeMount(() => {
    initialSetup();
    window.refreshYotpoData();
  })

  onMounted(() => {
    trackEvent('Viewed Product');
    assignRatingClickEvent();
  })

  const shopLocation = computed(() => {
    return window.shopLocation
  })

  const isTest = computed(() => {
    return product.template.includes('test') ? true : false;
  })

  const isMultiPack = computed(() => {
    return product.options.some(option => multiPackOptions.includes(option.name.toLowerCase()));
  });

  const yotpoProductId = computed(() => {
    const { tags } = product;
    const reviewTag = tags.find(tag => tag.startsWith("review-product-id="));
    if(!reviewTag) return product.id;
    
    return reviewTag.split('=')[1];
  })

  const accordianData = computed(() => {
    const resultObject = {};

    Object.entries(product).forEach(([key, value]) => {
      if (key.startsWith('accor_')) {
        if (Array.isArray(value)) {
          // Process array items
          value.forEach(item => {
            const { title, position } = item;
            resultObject[title] = resultObject[title] || { position, data: [] };
            resultObject[title].data.push(item);
          });
        } else if (value && typeof value === 'object') {
          // Process single object
          resultObject[value.title] = value;
        }
      }
    });

    if(selectedVariant.value.accor_how_and_when.length) {
      const selectedVariantHowAndWhenAccor = {
        position: selectedVariant.value.accor_how_and_when[0].position,
        data: selectedVariant.value.accor_how_and_when
      };
      resultObject[selectedVariant.value.accor_how_and_when[0].title] = selectedVariantHowAndWhenAccor;
    }

    const selectedVariantIngredientAccor = {
      position: "3",
      title: "Ingredients",
      content: selectedVariant.value.accor_ingredients
    };
    resultObject['Ingredients'] = selectedVariantIngredientAccor;

    // Convert resultObject to dataArray and sort by position
    const dataArray = Object.values(resultObject);
    dataArray.sort((a, b) => parseInt(a.position) - parseInt(b.position));

    // Convert sorted array back to object
    const sortedObject = {};
    dataArray.forEach(item => {
      if(item.data) {
        sortedObject[item.data[0].title] = item.data;
      }else {
        sortedObject[item.title] = item;
      }      
    });

    return sortedObject;
  });

  const initialSetup = () => {
    const params = new URLSearchParams(window.location.search);
    const variantParam = params.get('variant');
    selectedVariant.value = variantParam
      ? product.variants.find(variant => variant.id === variantParam)
      : product.variants[0];

    if(selectedVariant.value.has_subscription_plan){
      selectedSellingPlan.value = {
        name: selectedVariant.value.selling_plans[0].selling_plan.name,
        id: selectedVariant.value.selling_plans[0].selling_plan.id
      }
    }

    product.options.forEach((option, index) => {
      option.selected = selectedVariant.value.options[index];
    });

    updateURLParams('update', selectedVariant.value);
    dispatchCustomEvent();
  };

  const updateSelectedVariant = (index) => {
    const { options, variants } = product;
    selectedOptionIndex.value = index;

    const selectedOptions = options.map(option => option.selected.toLowerCase());

    selectedVariant.value = variants.find(variant => {
      return variant.options.every((option, idx) => option.toLowerCase() === selectedOptions[idx]);
    });

    if(!selectedVariant.value && isMultiPack.value && options.length === 3) {
      const availableVariants = variants.filter(variant => {
        return variant.options[0] === options[0].selected && variant.options[1] === options[1].selected
      });

      if (availableVariants.length) {
        selectedVariant.value = availableVariants[0];
        options.forEach((option, index) => {
          option.selected = selectedVariant.value.options[index];
        });
      }
    }

    if(!selectedVariant.value.has_subscription_plan && buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = false;
    }

    updateURLParams('update', selectedVariant.value);
    dispatchCustomEvent();
  };

  const onUpdateOption = (key,value, index) => {
    let tempSelectedOption = product.options[index].selected;
    product.options[index].selected = value;
    updateSelectedVariant(index);

    setTimeout(() => {
      if(key.toLowerCase() === 'size' && value == '20' && tempSelectedOption != value && buyboxSubscription.value) {
        buyboxSubscription.value.onSubscriptionChange(true)
      }
    })
  };

  const onUpdateSellingPlan = (value, isSubscription) => {
    selectedSellingPlan.value = value;

    if(isSubscription && buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = true;
    } 
  }

  const onUpdateIsSubscription = (value) => {
    if(buyboxSubscription.value) {
      buyboxSubscription.value.isSubscription = value;
    } 
  }

  const updateURLParams = (type, variant) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    if (type === 'update' && variant) {
      params.set('variant', variant.id);
    } else if (type === 'delete') {
      params.delete('color');
    }

    url.search = params.toString();
    window.history.replaceState({ url: url.toString() }, null, url.toString());
  };

  const dispatchCustomEvent = () => {
    const customEvent = new CustomEvent('selectedVariantUpdated', { detail: selectedVariant.value?.ingredient_spotlight });
    window.dispatchEvent(customEvent);
  }

  const trackEvent = (event) => {
    window.trackGTMEvent({
      product: {
        title: product.title,
        id: product.id,
        vendor: product.vendor,
        quantity: 1,
        selected_variant: selectedVariant.value,
        subscription: buyboxSubscription.value?.isSubscription ,
        selling_plan: selectedSellingPlan.value
      },
      event: event
    }) 
  }

  const assignRatingClickEvent = () => {
    document.querySelectorAll('.product__reviews').forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation();

        gsap.to(window, {
          duration: 1,
          scrollTo: {
            y: '#reviews',
            offsetY: 0
          }
        });

        return false
      }, {capture: true})
    })
  }

  defineExpose({selectedVariant})
</script>